import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import {
  ArrowLeft,
  Eye,
  Users,
  FileText,
  MessageSquare,
  Globe,
  Monitor,
  Tag,
  Clock,
  ChevronDown,
  ChevronRight,
  Activity,
  LogOut,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

interface Event {
  id: number
  name: string
  properties: Record<string, unknown>
  recorded_at: string
}

interface Visit {
  id: number
  visitor_token: string
  landing_page: string | null
  referrer: string | null
  browser: string | null
  os: string | null
  device_type: string | null
  utm_source: string | null
  started_at: string | null
  events: Event[]
}

interface Stats {
  total_visits: number
  unique_visitors: number
  page_views: number
  total_inquiries: number
}

function truncateToken(token: string) {
  return token.length > 10 ? token.slice(0, 10) + '…' : token
}

function pathOnly(url: string | null) {
  if (!url) return '—'
  try {
    return new URL(url).pathname
  } catch {
    return url
  }
}

function formatDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function StatCard({
  label,
  value,
  icon: Icon,
}: {
  label: string
  value: number
  icon: React.ElementType
}) {
  return (
    <Card className="border-brand-light shadow-sm">
      <CardContent className="pt-6">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-brand-red/10 p-2.5">
            <Icon className="h-5 w-5 text-brand-red" />
          </div>
          <div>
            <p className="text-sm font-medium text-brand-gray">{label}</p>
            <p className="text-2xl font-bold text-brand-dark">{value.toLocaleString()}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function EventRow({ event }: { event: Event }) {
  return (
    <tr className="border-b border-brand-light/60 last:border-0 bg-brand-light/30">
      <td className="py-2 px-4 pl-12 text-brand-dark font-medium text-xs">
        {event.name}
      </td>
      <td className="py-2 px-4 text-brand-gray text-xs font-mono whitespace-pre-wrap max-w-xs break-all">
        {Object.keys(event.properties).length > 0
          ? JSON.stringify(event.properties, null, 2)
          : '—'}
      </td>
      <td className="py-2 px-4 text-brand-gray text-xs">
        {formatDate(event.recorded_at)}
      </td>
    </tr>
  )
}

function VisitRow({ visit }: { visit: Visit }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <>
      <tr
        className="border-b border-brand-light last:border-0 hover:bg-brand-light/50 transition-colors cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <td className="py-3 px-4 text-brand-dark font-medium">
          <span className="inline-flex items-center gap-1.5">
            {expanded ? (
              <ChevronDown className="h-3.5 w-3.5 text-brand-gray" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-brand-gray" />
            )}
            <code className="text-xs bg-brand-light rounded px-1.5 py-0.5">
              {truncateToken(visit.visitor_token)}
            </code>
          </span>
        </td>
        <td className="py-3 px-4 text-brand-gray text-sm">
          {pathOnly(visit.landing_page)}
        </td>
        <td className="py-3 px-4 text-brand-gray text-sm">
          {visit.referrer || '—'}
        </td>
        <td className="py-3 px-4 text-brand-gray text-sm">
          {[visit.browser, visit.os, visit.device_type].filter(Boolean).join(' / ') || '—'}
        </td>
        <td className="py-3 px-4 text-brand-gray text-sm">
          {visit.utm_source || '—'}
        </td>
        <td className="py-3 px-4 text-brand-gray text-sm text-center">
          {visit.events.length}
        </td>
        <td className="py-3 px-4 text-brand-gray text-sm">
          {formatDate(visit.started_at)}
        </td>
      </tr>
      {expanded && visit.events.length > 0 && (
        <tr>
          <td colSpan={7} className="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b border-brand-light/60 bg-brand-light/20">
                  <th className="text-left py-2 px-4 pl-12 text-xs font-semibold text-brand-gray">
                    Event
                  </th>
                  <th className="text-left py-2 px-4 text-xs font-semibold text-brand-gray">
                    Properties
                  </th>
                  <th className="text-left py-2 px-4 text-xs font-semibold text-brand-gray">
                    Recorded At
                  </th>
                </tr>
              </thead>
              <tbody>
                {visit.events.map((event) => (
                  <EventRow key={event.id} event={event} />
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  )
}

export default function Index({ stats, visits }: { stats: Stats; visits: Visit[] }) {
  return (
    <div className="min-h-screen bg-brand-light font-montserrat text-brand-dark">
      <header className="bg-brand-dark py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <img
                src="/curbtek-logo.png"
                alt="CurbTek Logo"
                className="h-10 object-contain"
              />
            </div>
            <div className="flex items-center gap-6">
              <Link
                href="/admin/inquiries"
                className="inline-flex items-center gap-2 text-sm font-semibold text-gray-300 hover:text-white transition-colors"
              >
                <MessageSquare className="h-4 w-4" />
                Inquiries
              </Link>
              <Link
                href="/admin"
                className="inline-flex items-center gap-2 text-sm font-semibold text-gray-300 hover:text-white transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Admin Home
              </Link>
              <button
                onClick={() => router.delete('/session')}
                className="inline-flex items-center gap-2 text-sm font-semibold text-gray-300 hover:text-white transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Log Out
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Total Visits" value={stats.total_visits} icon={Eye} />
          <StatCard label="Unique Visitors" value={stats.unique_visitors} icon={Users} />
          <StatCard label="Page Views" value={stats.page_views} icon={FileText} />
          <StatCard label="Inquiries" value={stats.total_inquiries} icon={MessageSquare} />
        </div>

        <Card className="border-brand-light shadow-lg">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-brand-dark">
              Visits
            </CardTitle>
          </CardHeader>
          <CardContent>
            {visits.length === 0 ? (
              <p className="text-brand-gray text-center py-12">
                No visits in the last 30 days.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-brand-light">
                      <th className="text-left py-3 px-4 font-semibold text-brand-gray">
                        <span className="inline-flex items-center gap-1.5">
                          <Users className="h-3.5 w-3.5" /> Visitor
                        </span>
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-brand-gray">
                        <span className="inline-flex items-center gap-1.5">
                          <Globe className="h-3.5 w-3.5" /> Landing Page
                        </span>
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-brand-gray">
                        <span className="inline-flex items-center gap-1.5">
                          <ArrowLeft className="h-3.5 w-3.5" /> Referrer
                        </span>
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-brand-gray">
                        <span className="inline-flex items-center gap-1.5">
                          <Monitor className="h-3.5 w-3.5" /> Browser / OS / Device
                        </span>
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-brand-gray">
                        <span className="inline-flex items-center gap-1.5">
                          <Tag className="h-3.5 w-3.5" /> UTM Source
                        </span>
                      </th>
                      <th className="text-center py-3 px-4 font-semibold text-brand-gray">
                        <span className="inline-flex items-center gap-1.5">
                          <Activity className="h-3.5 w-3.5" /> Events
                        </span>
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-brand-gray">
                        <span className="inline-flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5" /> Started At
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visits.map((visit) => (
                      <VisitRow key={visit.id} visit={visit} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  )
}
